"use client";

import type React from "react";

import Header from "@/components/layouts/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import DateField from "@/components/ui/date-field";
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ReportButton } from "@/components/ui/report-button";
import VehicleSelect from "@/components/ui/vehicle-select";
import { errorHandler, toggleVisibility } from "@/lib/utils";
import {
  type ExpensesReportResponse,
  expensesReportService,
} from "@/services/api/expensesReport";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Check, CheckIcon, CheckSquare } from "lucide-react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import ExpensesReportTable from "./ExpensesReportTable";

const formSchema = z
  .object({
    vehicle_id: z.array(z.number()).min(1, "Selecione ao menos um veículo"),
    startDate: z.string().date("Data inicial é obrigatória"),
    endDate: z.string().date("Data final é obrigatória"),
    categories: z.array(z.string()).min(1, "Selecione ao menos uma categoria"),
  })
  .refine(
    (data) => {
      return new Date(data.endDate) >= new Date(data.startDate);
    },
    {
      message: "A data final deve ser maior ou igual à data inicial",
      path: ["endDate"],
    },
  );

type FormValues = z.infer<typeof formSchema>;

const ExpensesReport = () => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const endOfDay = new Date(today);
  endOfDay.setHours(23, 59, 59, 999);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      vehicle_id: [],
      startDate: format(firstDayOfMonth, "yyyy-MM-dd"),
      endDate: format(endOfDay, "yyyy-MM-dd"),
      categories: [],
    },
  });
  const [includeAll, setIncludeAll] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(true);
  const [reportData, setReportData] = useState<ExpensesReportResponse | null>(
    null,
  );

  const {
    handleSubmit,
    formState: { isSubmitting },
    watch,
    setValue,
  } = form;

  const selectedCategories = watch("categories");

  const expenseCategories = [
    {
      id: "fuelling",
      name: "abastecimentos",
      label: "Abastecimentos",
      fields: [
        { key: "date", label: "Data e hora", type: "datetime" },
        { key: "odometer", label: "Odômetro", type: "number" },
        { key: "liters", label: "Litros", type: "number" },
        { key: "cost_per_liter", label: "R$/Litro", type: "currency" },
        { key: "cost", label: "R$", type: "currency" },
        { key: "fuel_type.name", label: "Combustível", type: "string" },
        { key: "arla_liters", label: "Litros Arla", type: "number" },
        { key: "arla_price", label: "Preço Arla", type: "currency" },
        { key: "arla_amount", label: "Valor Arla", type: "currency" },
        { key: "provider.name", label: "Fornecedor", type: "string" },
        { key: "driver.name", label: "Motorista", type: "string" },
      ],
    },
    {
      id: "licensing",
      name: "licenciamentos",
      label: "Licenciamentos",
      fields: [
        { key: "payment_date", label: "Pagamento", type: "date" },
        { key: "ipva", label: "IPVA", type: "currency" },
        { key: "dpvat", label: "DPVAT", type: "currency" },
        { key: "expedition", label: "T. de expedição", type: "currency" },
        { key: "licensing", label: "Licenciamento", type: "currency" },
      ],
    },
    {
      id: "maintenance",
      name: "manutencoes",
      label: "Manutenções",
      fields: [
        { key: "occurrence_date", label: "Data", type: "date" },
        { key: "description", label: "Descrição", type: "string" },
        { key: "value", label: "Valor (R$)", type: "currency" },
        { key: "odometer", label: "Odômetro", type: "number" },
        { key: "client_provider.name", label: "Fornecedor", type: "string" },
        { key: "invoice", label: "Nota fiscal", type: "string" },
      ],
    },
    {
      id: "ticket",
      name: "multas",
      label: "Multas",
      fields: [
        { key: "payment_date", label: "Pagamento", type: "date" },
        { key: "driver.name", label: "Motorista", type: "string" },
        { key: "paid_amount", label: "Valor pago (R$)", type: "currency" },
        { key: "date_time", label: "Data e hora", type: "datetime" },
        { key: "classification", label: "Classificação", type: "string" },
        { key: "local", label: "Local", type: "string" },
      ],
    },
    {
      id: "otherFees",
      name: "outrasTaxas",
      label: "Outras taxas",
      fields: [
        { key: "payment_date", label: "Pagamento", type: "date" },
        { key: "name", label: "Nome", type: "string" },
        { key: "amount", label: "Valor (R$)", type: "currency" },
      ],
    },
    {
      id: "repair",
      name: "reparos",
      label: "Reparos",
      fields: [
        { key: "date", label: "Data", type: "date" },
        { key: "description", label: "Descrição", type: "string" },
        { key: "amount", label: "Valor (R$)", type: "currency" },
        { key: "odometer", label: "Odômetro", type: "number" },
        { key: "provider.name", label: "Fornecedor", type: "string" },
        { key: "invoice", label: "Nota fiscal", type: "string" },
      ],
    },
    {
      id: "insurance",
      name: "seguros",
      label: "Seguros",
      fields: [
        { key: "payment_date", label: "Pagamento", type: "date" },
        { key: "company", label: "Seguradora", type: "string" },
        { key: "policy", label: "Apólice", type: "string" },
        { key: "premium", label: "Prêmio", type: "currency" },
        { key: "deductible", label: "Franquia", type: "currency" },
        {
          key: "deductibles.length",
          label: "Número de franquias",
          type: "number",
        },
      ],
    },
  ];

  const handleToggleAll = (checked: boolean) => {
    setIncludeAll(checked);
    if (checked) {
      const allCategories = expenseCategories.map((cat) => cat.id);
      setValue("categories", allCategories);
    } else {
      setValue("categories", []);
    }
  };

  const handleToggleCategory = (categoryId: string) => {
    const currentCategories = [...selectedCategories];
    if (currentCategories.includes(categoryId)) {
      setValue(
        "categories",
        currentCategories.filter((id) => id !== categoryId),
      );
    } else {
      setValue("categories", [...currentCategories, categoryId]);
    }
  };

  const handleGetReport = async (data: FormValues) => {
    try {
      const params = {
        vehicle_id: data.vehicle_id.join(","),
        start_date: data.startDate,
        end_date: data.endDate,
        categories: data.categories.join(","),
      };

      const responseData =
        await expensesReportService.getExpensesReport(params);
      setReportData(responseData);
      setIsFormVisible(false);
    } catch (error) {
      errorHandler(error, "Erro ao gerar o relatório.");
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    form.trigger();

    const categoriesValue = form.getValues("categories");
    if (categoriesValue.length === 0) {
      toast.error("Selecione ao menos uma categoria");
      return;
    }

    handleSubmit(handleGetReport)();
  };

  const handleToggleFormVisibility = () => {
    toggleVisibility(isFormVisible, setIsFormVisible);
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col overflow-y-auto">
      <div className="flex items-center gap-2">
        <Header
          title="Despesas"
          showToggleButton
          isFormVisible={isFormVisible}
          onToggleFormVisibility={handleToggleFormVisibility}
        />
      </div>
      {isFormVisible && (
        <FormProvider {...form}>
          <form id="expensesForm" onSubmit={onSubmit}>
            <div>
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <div className="flex flex-col space-y-4">
                    <FormField
                      control={form.control}
                      name="vehicle_id"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Veículo</FormLabel>
                          <VehicleSelect
                            value={field.value}
                            onChange={field.onChange}
                            multiple
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="filter-item">
                      <DateField form={form} name="startDate" label="De" />
                    </div>
                    <div className="filter-item">
                      <DateField form={form} name="endDate" label="Até" />
                    </div>
                  </div>

                  <div className="mb-6 mt-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                      <h3 className="text-lg font-medium mb-3 sm:mb-0">
                        Categorias de despesas
                      </h3>
                      <Button
                        type="button"
                        variant="default"
                        onClick={() => handleToggleAll(!includeAll)}
                        className={`h-10 px-4 font-medium text-white ${
                          includeAll
                            ? "bg-orange-500 hover:bg-orange-600"
                            : "bg-blue-500 hover:bg-blue-600"
                        }`}
                      >
                        {includeAll ? (
                          <Check className="mr-2 h-4 w-4" />
                        ) : (
                          <CheckSquare className="mr-2 h-4 w-4" />
                        )}
                        {includeAll ? "Desmarcar todas" : "Selecionar todas"}
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-2">
                      {expenseCategories.map((category) => {
                        const isSelected = selectedCategories.includes(
                          category.id,
                        );
                        return (
                          <div
                            key={category.id}
                            onClick={() => handleToggleCategory(category.id)}
                            className={`cursor-pointer rounded-lg border p-3 transition-all hover:shadow-md ${
                              isSelected
                                ? "bg-blue-500 border-blue-600 text-white"
                                : "bg-card border-muted"
                            }`}
                          >
                            <div className="flex flex-col items-center text-center">
                              <div className="text-sm font-medium flex items-center">
                                {isSelected && (
                                  <CheckIcon className="h-4 w-8 ml-2" />
                                )}
                                {category.label}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <ReportButton loading={isSubmitting} />
              </div>
            </div>
          </form>
        </FormProvider>
      )}

      {reportData && !isFormVisible && (
        <ExpensesReportTable
          data={reportData.data}
          startDate={form.getValues("startDate")}
          endDate={form.getValues("endDate")}
          expenseCategories={expenseCategories}
        />
      )}
    </div>
  );
};

export default ExpensesReport;
